/*
 * Forma do bloco env do ~/.claude/settings.json (incluindo CLAUDE_CODE_ENABLE_GATEWAY_MODEL_DISCOVERY)
 * portada do OmniRoute (MIT) -- Copyright (c) 2026 diegosouzapw
 * Origem: omniroute@18bbb10:src/lib/cli-helper/config-generator/claude.ts:6-29.
 * Aviso de licenca integral: THIRD_PARTY_NOTICES.md na raiz do repositorio.
 * Adaptado: sem ANTHROPIC_MODEL, sem escrita de arquivo e SEM normalizacao/composicao de base (D32).
 */

/**
 * Connection snippets for the opus-ia gateway -- the ONLY snippet generator of the app
 * (FEAT-033 SPEC.md §11; D32 addendum of 2026-09-30, FEAT-035 TASK-013).
 *
 * Pure module (no React, no DOM): every screen that shows "how to connect" renders
 * exclusively what buildConnectSnippets returns -- no component writes ANTHROPIC_BASE_URL
 * & co. by hand.
 *
 * D32: the browser NEVER composes the gateway base. The input is `baseUrls` exactly as the API
 * returns it (`base_urls` of /me/api-keys, /api-keys, /admin/connections/endpoints, composed
 * server-side by buildBaseUrls in api/src/services/connections/gatewayUrls.ts) and it is used
 * VERBATIM: no origin, no path constant, no normalization here.
 *
 * Endpoint URL of the curl examples = baseUrls.claude_code + catalog path (/v1/messages,
 * /v1/chat/completions). baseUrls.openai only appears as a value to paste into OpenAI clients
 * (Codex base_url) -- appending a path to it would produce /v1/v1.
 *
 * maskSecret is the ONLY masker of the application. The mask shows the real prefix followed
 * by an ellipsis and NEVER the end of the secret (showing the tail of a secret is exposure
 * with no gain). There is no "reveal" function: at rest only sha256(secret) exists, so a
 * stored secret cannot be shown again by construction.
 */

/** anthropic-version header used by the curl example. */
export const ANTHROPIC_VERSION = '2023-06-01';

/** Default catalog paths (GATEWAY_ENDPOINT_CATALOG of the contract) when the caller has no live catalog. */
export const DEFAULT_MESSAGES_PATH = '/v1/messages';
export const DEFAULT_CHAT_PATH = '/v1/chat/completions';

const ELLIPSIS = '…';

/** Fallback prefix when a key prefix is somehow missing -- never prints "undefined"/"null". */
const FALLBACK_PREFIX = 'oia_';

/**
 * Masked display of a key: the real prefix (oia_ + 8 chars) followed by an ellipsis.
 * e.g. maskSecret('oia_Ab3dEf9h') === 'oia_Ab3dEf9h…'
 */
export function maskSecret(keyPrefix: string | null | undefined): string {
  const prefix = typeof keyPrefix === 'string' && keyPrefix.trim() !== '' ? keyPrefix.trim() : FALLBACK_PREFIX;
  return `${prefix}${ELLIPSIS}`;
}

/** `base_urls` of the API (contracts/api-keys-endpoints.yaml shared_schemas.GatewayBaseUrls). */
export interface GatewayBaseUrls {
  /** Value of ANTHROPIC_BASE_URL (Claude Code, Anthropic SDK). */
  claude_code: string;
  /** OpenAI-compatible base (already ends in the version segment); pasted verbatim as base_url in the Codex config.toml. */
  openai: string;
}

export interface ConnectSnippetsInput {
  /** base_urls from the API, used verbatim. */
  baseUrls: GatewayBaseUrls;
  /** Full secret right after create/rotate, or null for the masked mode. */
  secret: string | null;
  /** key_prefix of the key -- feeds maskSecret when there is no secret (null -> `oia_…`). */
  keyPrefix: string | null;
  /** Model shown in the curl bodies (default: an ellipsis -- never a fixed model). */
  model?: string;
  /** Live catalog paths for the curl examples (default: DEFAULT_MESSAGES_PATH / DEFAULT_CHAT_PATH). */
  paths?: { messages?: string; chatCompletions?: string };
}

// --- FEAT-044 TASK-007 (Codex e Cursor) ---
/** i18n id of the single Cursor notice shown by both screens (no steps, no base, no key). */
export const CURSOR_UNDOCUMENTED_MESSAGE_ID = 'gateway.connect.cursor.undocumented';

export interface CursorNotice {
  messageId: typeof CURSOR_UNDOCUMENTED_MESSAGE_ID;
}
// --- /FEAT-044 TASK-007 (Codex e Cursor) ---

export interface ConnectSnippets {
  /** Shell exports + `claude`. */
  claudeCodeEnv: string;
  /** Content of ~/.claude/settings.json. */
  claudeCodeSettingsJson: string;
  /** ~/.codex/config.toml + terminal export (name kept as codexEnv for compatibility). */
  codexEnv: string;
  /** Cursor: unofficial integration notice (i18n id only). */
  cursorNotice: CursorNotice;
  /** curl example against /v1/messages (Anthropic protocol, x-api-key). */
  curl: string;
  /** curl example against /v1/chat/completions (OpenAI protocol, Authorization: Bearer). */
  curlOpenai: string;
}

export function buildConnectSnippets({
  baseUrls,
  secret,
  keyPrefix,
  model = ELLIPSIS,
  paths,
}: ConnectSnippetsInput): ConnectSnippets {
  const anthropicBaseUrl = baseUrls.claude_code;
  const openaiBaseUrl = baseUrls.openai;
  const messagesPath = paths?.messages ?? DEFAULT_MESSAGES_PATH;
  const chatPath = paths?.chatCompletions ?? DEFAULT_CHAT_PATH;
  const token = typeof secret === 'string' && secret !== '' ? secret : maskSecret(keyPrefix);

  const claudeCodeEnv = [
    `export ANTHROPIC_BASE_URL="${anthropicBaseUrl}"`,
    `export ANTHROPIC_AUTH_TOKEN="${token}"`,
    'export CLAUDE_CODE_ENABLE_GATEWAY_MODEL_DISCOVERY=1',
    'claude',
  ].join('\n');

  const claudeCodeSettingsJson = JSON.stringify(
    {
      env: {
        ANTHROPIC_BASE_URL: anthropicBaseUrl,
        ANTHROPIC_AUTH_TOKEN: token,
        CLAUDE_CODE_ENABLE_GATEWAY_MODEL_DISCOVERY: '1',
      },
    },
    null,
    2,
  );

  // --- FEAT-044 TASK-007 (Codex e Cursor) ---
  // codexEnv keeps its name for compatibility; the content is the documented config.toml.
  const codexEnv = [
    '# ~/.codex/config.toml',
    'model_provider = "opus"',
    `model = "${model}"`,
    '',
    '[model_providers.opus]',
    'name = "opus-ia"',
    `base_url = "${openaiBaseUrl}"`,
    'env_key = "OPUS_IA_API_KEY"',
    'wire_api = "responses"',
    '',
    '# terminal',
    `export OPUS_IA_API_KEY="${token}"`,
    'codex',
  ].join('\n');

  const cursorNotice: CursorNotice = { messageId: CURSOR_UNDOCUMENTED_MESSAGE_ID };
  // --- /FEAT-044 TASK-007 (Codex e Cursor) ---

  const curl = [
    `curl ${anthropicBaseUrl}${messagesPath} \\`,
    `  -H "x-api-key: ${token}" \\`,
    `  -H "anthropic-version: ${ANTHROPIC_VERSION}" \\`,
    '  -H "content-type: application/json" \\',
    `  -d '${JSON.stringify({ model, max_tokens: 64, messages: [{ role: 'user', content: 'olá' }] })}'`,
  ].join('\n');

  const curlOpenai = [
    `curl ${anthropicBaseUrl}${chatPath} \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    '  -H "content-type: application/json" \\',
    `  -d '${JSON.stringify({ model, messages: [{ role: 'user', content: 'olá' }] })}'`,
  ].join('\n');

  return { claudeCodeEnv, claudeCodeSettingsJson, codexEnv, cursorNotice, curl, curlOpenai };
}

